'use client';

import React, { useState, useEffect } from 'react';
// NOTE: Do NOT use next/link Link components - this tool uses R2 bundle loading
// Using regular <a> tags instead for compatibility
import { CalculatorCard, CalculatorSection } from './CalculatorCard';
import { MetricCard } from './ResultsDisplay';
import { SelectInput, DualInputWithSlider } from './FormInputs';
import { InfoIcon } from './Tooltip';
import type { IndustrySector, GeographicRegion, SecurityMaturityLevel, BreachCostResult, DataType } from '@/types/calculator';
import { generateCyberInsurancePDF } from '@/lib/calculators/cyber-insurance-pdf';

// ============================================================================
// TYPES
// ============================================================================

interface CyberInsuranceTabProps {
  industry: IndustrySector;
  region: GeographicRegion;
  annualRevenue: number;
  recordCount: number;
  maturityLevel: SecurityMaturityLevel;
  hasIR: boolean;
  breachCostResult: BreachCostResult | null;
  dataTypes?: DataType[];
}

interface InsuranceRecommendation {
  recommendedCoverageLimit: number;
  coverageRange: { min: number; max: number };
  estimatedPremium: { low: number; high: number };
  deductibleRange: { low: number; high: number };
  coverageTypes: {
    firstParty: CoverageItem[];
    thirdParty: CoverageItem[];
    endorsements: CoverageItem[];
  };
  premiumFactors: PremiumFactor[];
  readinessScore: number;
  readinessItems: ReadinessItem[];
  roi: {
    premiumCost: number;
    potentialPayout: number;
    outOfPocketWithout: number;
    outOfPocketWith: number;
    savingsRatio: number;
  };
  questionsForInsurer: QuestionForInsurer[];
}

interface QuestionForInsurer {
  category: string;
  question: string;
  whyItMatters: string;
}

interface CoverageItem {
  name: string;
  description: string;
  recommended: boolean;
  estimatedValue?: string;
}

interface PremiumFactor {
  factor: string;
  impact: 'increase' | 'decrease' | 'neutral';
  description: string;
  percentage?: number;
}

interface ReadinessItem {
  id: string;
  name: string;
  description: string;
  implemented: boolean;
  impact: 'critical' | 'high' | 'medium';
  premiumImpact: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const INDUSTRY_PREMIUM_MULTIPLIERS: Record<IndustrySector, number> = {
  healthcare: 2.2,
  pharmaceutical: 1.9,
  financial: 1.8,
  technology: 1.4,
  energy: 1.5,
  industrial: 1.1,
  manufacturing: 1.2,
  retail: 1.4,
  hospitality: 1.3,
  media: 1.2,
  education: 1.3,
  professional: 1.2,
  government: 1.0,
  other: 1.0
};

const GEOGRAPHIC_SCOPE_OPTIONS = [
  { value: 'us_only', label: 'United States Only' },
  { value: 'north_america', label: 'North America (US + Canada)' },
  { value: 'international', label: 'International Operations' }
];

const PRIOR_BREACH_OPTIONS = [
  { value: 'none', label: 'No prior breaches' },
  { value: 'over_3_years', label: 'Breach over 3 years ago' },
  { value: 'within_3_years', label: 'Breach within past 3 years' },
  { value: 'within_1_year', label: 'Breach within past year' }
];

// ============================================================================
// CALCULATION FUNCTIONS
// ============================================================================

function calculateInsuranceRecommendation(
  industry: IndustrySector,
  region: GeographicRegion,
  annualRevenue: number,
  recordCount: number,
  maturityLevel: SecurityMaturityLevel,
  hasIR: boolean,
  geographicScope: string,
  priorBreach: string,
  readinessChecklist: Record<string, boolean>,
  potentialBreachCost: number,
  dataTypes: DataType[],
  annualTransactionVolume: number
): InsuranceRecommendation {
  // Calculate recommended coverage limit
  // Rule of thumb: Coverage should be at least 2x likely breach cost, capped at reasonable limits
  const baseLimit = Math.max(potentialBreachCost * 1.5, 1000000);
  const maxLimit = Math.min(annualRevenue * 0.1, 50000000); // 10% of revenue, max $50M
  const recommendedLimit = Math.min(baseLimit, maxLimit);

  // Coverage range
  const coverageRange = {
    min: Math.max(recommendedLimit * 0.5, 1000000),
    max: Math.min(recommendedLimit * 2, 100000000)
  };

  // Calculate premium
  // Base rate: 0.5-2% of coverage limit for most businesses
  const industryMult = INDUSTRY_PREMIUM_MULTIPLIERS[industry];
  const baseRate = 0.01; // 1% base rate

  // Adjust for factors
  let premiumMultiplier = industryMult;

  // Revenue factor
  if (annualRevenue > 100000000) premiumMultiplier *= 0.85; // Volume discount
  else if (annualRevenue < 10000000) premiumMultiplier *= 1.2; // Small business premium

  // Records factor
  if (recordCount > 1000000) premiumMultiplier *= 1.3;
  else if (recordCount > 100000) premiumMultiplier *= 1.1;

  // Data types factor - sensitive data increases risk
  const hasPHI = dataTypes.includes('phi');
  const hasPCI = dataTypes.includes('pci');
  const hasSSN = dataTypes.includes('ssn_tax_id');
  const hasIP = dataTypes.includes('ip_trade_secrets');
  const hasFinancial = dataTypes.includes('financial_records');

  if (hasPHI) premiumMultiplier *= 1.25; // HIPAA exposure
  if (hasPCI) premiumMultiplier *= 1.2; // PCI DSS exposure
  if (hasSSN) premiumMultiplier *= 1.15; // Identity theft risk
  if (hasIP) premiumMultiplier *= 1.1; // Valuable target
  if (hasFinancial) premiumMultiplier *= 1.1; // Financial fraud risk

  // Transaction volume factor
  if (annualTransactionVolume > 10000000) premiumMultiplier *= 1.3; // High volume
  else if (annualTransactionVolume > 1000000) premiumMultiplier *= 1.15;
  else if (annualTransactionVolume > 100000) premiumMultiplier *= 1.05;

  // Geographic scope
  if (geographicScope === 'international') premiumMultiplier *= 1.4;
  else if (geographicScope === 'north_america') premiumMultiplier *= 1.15;

  // Prior breach history
  if (priorBreach === 'within_1_year') premiumMultiplier *= 2.0;
  else if (priorBreach === 'within_3_years') premiumMultiplier *= 1.5;
  else if (priorBreach === 'over_3_years') premiumMultiplier *= 1.1;

  // Security maturity discount
  if (maturityLevel === 'optimized') premiumMultiplier *= 0.7;
  else if (maturityLevel === 'advanced') premiumMultiplier *= 0.8;
  else if (maturityLevel === 'moderate') premiumMultiplier *= 0.9;
  else if (maturityLevel === 'basic') premiumMultiplier *= 1.1;
  else if (maturityLevel === 'minimal') premiumMultiplier *= 1.3;

  // Readiness checklist discounts
  const readinessItems = Object.entries(readinessChecklist);
  const implementedCount = readinessItems.filter(([, v]) => v).length;
  const readinessDiscount = Math.min(implementedCount * 0.03, 0.25); // Up to 25% discount
  premiumMultiplier *= (1 - readinessDiscount);

  const estimatedPremium = {
    low: Math.round(recommendedLimit * baseRate * premiumMultiplier * 0.8),
    high: Math.round(recommendedLimit * baseRate * premiumMultiplier * 1.2)
  };

  // Deductible range (typically 1-5% of coverage)
  const deductibleRange = {
    low: Math.max(10000, recommendedLimit * 0.01),
    high: Math.min(500000, recommendedLimit * 0.05)
  };

  // Coverage types
  const coverageTypes = {
    firstParty: [
      {
        name: 'Business Interruption',
        description: 'Covers lost income and extra expenses during system downtime',
        recommended: true,
        estimatedValue: `Up to ${formatCurrency(annualRevenue * 0.1)}/incident`
      },
      {
        name: 'Data Recovery',
        description: 'Costs to restore or recreate lost or corrupted data',
        recommended: true,
        estimatedValue: 'Typically $50K-500K'
      },
      {
        name: 'Ransomware/Extortion Payments',
        description: 'Coverage for ransom demands and negotiation services',
        recommended: recordCount > 10000,
        estimatedValue: 'Varies by incident'
      },
      {
        name: 'Crisis Management',
        description: 'PR and communications support during incidents',
        recommended: annualRevenue > 10000000,
        estimatedValue: 'Typically $100K-500K'
      }
    ],
    thirdParty: [
      {
        name: 'Privacy Liability',
        description: 'Defense and settlement costs for data breach lawsuits',
        recommended: true,
        estimatedValue: `Up to ${formatCurrency(recommendedLimit * 0.5)}`
      },
      {
        name: 'Regulatory Fines & Penalties',
        description: 'Coverage for GDPR, HIPAA, CCPA fines where insurable',
        recommended: industry === 'healthcare' || industry === 'financial' || region === 'germany' || region === 'uk' || hasPHI || hasPCI,
        estimatedValue: 'Varies by regulation'
      },
      {
        name: 'Notification Costs',
        description: 'Mandatory breach notifications, credit monitoring, call centers',
        recommended: true,
        estimatedValue: `$${Math.round(recordCount * 15).toLocaleString()} estimated`
      },
      {
        name: 'PCI DSS Fines & Assessments',
        description: 'Card brand fines for payment data breaches',
        recommended: industry === 'retail' || industry === 'hospitality' || hasPCI,
        estimatedValue: 'Up to $5M'
      },
      {
        name: 'HIPAA Breach Coverage',
        description: 'Specific coverage for healthcare data breaches and OCR investigations',
        recommended: hasPHI || industry === 'healthcare',
        estimatedValue: 'Up to $2M per incident'
      }
    ],
    endorsements: [
      {
        name: 'Social Engineering/Wire Fraud',
        description: 'Losses from business email compromise and phishing scams',
        recommended: true,
        estimatedValue: 'Typically $100K-500K sublimit'
      },
      {
        name: 'System Failure Coverage',
        description: 'Outages not caused by cyberattacks (hardware, software failures)',
        recommended: industry === 'technology' || industry === 'financial',
        estimatedValue: 'Added premium of 10-20%'
      },
      {
        name: 'Reputational Harm',
        description: 'Extended coverage for brand damage and customer churn',
        recommended: annualRevenue > 50000000,
        estimatedValue: 'Varies significantly'
      },
      {
        name: 'Contingent Business Interruption',
        description: 'Coverage for outages at vendors/suppliers',
        recommended: industry === 'manufacturing' || industry === 'retail',
        estimatedValue: 'Added premium of 15-25%'
      }
    ]
  };

  // Premium factors analysis
  const premiumFactors: PremiumFactor[] = [];

  // Industry
  if (industryMult > 1.5) {
    premiumFactors.push({
      factor: `${industry.charAt(0).toUpperCase() + industry.slice(1)} Industry`,
      impact: 'increase',
      description: 'High-risk industry classification',
      percentage: Math.round((industryMult - 1) * 100)
    });
  } else if (industryMult < 1.0) {
    premiumFactors.push({
      factor: `${industry.charAt(0).toUpperCase() + industry.slice(1)} Industry`,
      impact: 'decrease',
      description: 'Lower-risk industry classification',
      percentage: Math.round((1 - industryMult) * 100)
    });
  }

  // Security maturity
  if (maturityLevel === 'advanced' || maturityLevel === 'optimized') {
    premiumFactors.push({
      factor: 'Strong Security Posture',
      impact: 'decrease',
      description: `${maturityLevel === 'optimized' ? 'Optimized' : 'Advanced'} security maturity level`,
      percentage: maturityLevel === 'optimized' ? 30 : 20
    });
  } else if (maturityLevel === 'minimal' || maturityLevel === 'basic') {
    premiumFactors.push({
      factor: 'Security Maturity Gap',
      impact: 'increase',
      description: 'Below-average security controls may increase premiums',
      percentage: maturityLevel === 'minimal' ? 30 : 10
    });
  }

  // Prior breach
  if (priorBreach !== 'none') {
    premiumFactors.push({
      factor: 'Prior Breach History',
      impact: 'increase',
      description: 'Past incidents increase perceived risk',
      percentage: priorBreach === 'within_1_year' ? 100 : priorBreach === 'within_3_years' ? 50 : 10
    });
  }

  // Geographic scope
  if (geographicScope === 'international') {
    premiumFactors.push({
      factor: 'International Operations',
      impact: 'increase',
      description: 'Multi-jurisdiction regulatory exposure',
      percentage: 40
    });
  }

  // Readiness items
  if (implementedCount >= 5) {
    premiumFactors.push({
      factor: 'Strong Security Controls',
      impact: 'decrease',
      description: 'Implemented security requirements for insurability',
      percentage: Math.round(readinessDiscount * 100)
    });
  }

  // Data types impact
  const sensitiveDataTypes: string[] = [];
  if (hasPHI) sensitiveDataTypes.push('PHI (HIPAA)');
  if (hasPCI) sensitiveDataTypes.push('PCI data');
  if (hasSSN) sensitiveDataTypes.push('SSN/Tax IDs');
  if (hasFinancial) sensitiveDataTypes.push('Financial records');
  if (hasIP) sensitiveDataTypes.push('Intellectual property');

  if (sensitiveDataTypes.length > 0) {
    premiumFactors.push({
      factor: 'Sensitive Data Types',
      impact: 'increase',
      description: `Handling: ${sensitiveDataTypes.join(', ')}`,
      percentage: Math.round((sensitiveDataTypes.length * 12))
    });
  }

  // Transaction volume
  if (annualTransactionVolume > 1000000) {
    premiumFactors.push({
      factor: 'High Transaction Volume',
      impact: 'increase',
      description: `${(annualTransactionVolume / 1000000).toFixed(1)}M+ transactions/year`,
      percentage: annualTransactionVolume > 10000000 ? 30 : 15
    });
  }

  // Insurance readiness checklist
  const readinessItemsList: ReadinessItem[] = [
    {
      id: 'mfa',
      name: 'Multi-Factor Authentication (MFA)',
      description: 'MFA enabled for all remote access and privileged accounts',
      implemented: readinessChecklist.mfa || false,
      impact: 'critical',
      premiumImpact: 'Required by most insurers; can reduce premium by 5-10%'
    },
    {
      id: 'edr',
      name: 'Endpoint Detection & Response (EDR)',
      description: 'EDR deployed on all endpoints with 24/7 monitoring',
      implemented: readinessChecklist.edr || false,
      impact: 'critical',
      premiumImpact: 'Often required; reduces premium by 5-10%'
    },
    {
      id: 'backup',
      name: 'Offline/Immutable Backups',
      description: 'Regular backups stored offline or immutable, tested quarterly',
      implemented: readinessChecklist.backup || false,
      impact: 'critical',
      premiumImpact: 'Required for ransomware coverage; reduces premium by 5-10%'
    },
    {
      id: 'irPlan',
      name: 'Incident Response Plan',
      description: 'Documented IR plan tested at least annually',
      implemented: hasIR || readinessChecklist.irPlan || false,
      impact: 'high',
      premiumImpact: 'Reduces premium by 3-5%'
    },
    {
      id: 'training',
      name: 'Security Awareness Training',
      description: 'Regular phishing simulations and security training for all employees',
      implemented: readinessChecklist.training || false,
      impact: 'high',
      premiumImpact: 'Reduces premium by 2-5%'
    },
    {
      id: 'patching',
      name: 'Vulnerability Management',
      description: 'Regular vulnerability scanning with critical patches applied within 30 days',
      implemented: readinessChecklist.patching || false,
      impact: 'high',
      premiumImpact: 'Reduces premium by 3-5%'
    },
    {
      id: 'encryption',
      name: 'Data Encryption',
      description: 'Encryption at rest and in transit for sensitive data',
      implemented: readinessChecklist.encryption || false,
      impact: 'medium',
      premiumImpact: 'May reduce premium by 2-3%'
    },
    {
      id: 'accessControl',
      name: 'Privileged Access Management',
      description: 'Least privilege access, separate admin accounts, access reviews',
      implemented: readinessChecklist.accessControl || false,
      impact: 'medium',
      premiumImpact: 'May reduce premium by 2-3%'
    }
  ];

  // Calculate readiness score
  const criticalItems = readinessItemsList.filter(i => i.impact === 'critical');
  const criticalImplemented = criticalItems.filter(i => i.implemented).length;
  const highItems = readinessItemsList.filter(i => i.impact === 'high');
  const highImplemented = highItems.filter(i => i.implemented).length;
  const mediumItems = readinessItemsList.filter(i => i.impact === 'medium');
  const mediumImplemented = mediumItems.filter(i => i.implemented).length;

  const readinessScore = Math.round(
    (criticalImplemented / criticalItems.length * 50) +
    (highImplemented / highItems.length * 35) +
    (mediumImplemented / mediumItems.length * 15)
  );

  // ROI calculation
  const avgPremium = (estimatedPremium.low + estimatedPremium.high) / 2;
  const avgDeductible = (deductibleRange.low + deductibleRange.high) / 2;
  const outOfPocketWithout = potentialBreachCost;
  const outOfPocketWith = avgDeductible + avgPremium; // Deductible + premium for one year
  const typicalPayout = potentialBreachCost * 0.65; // 65% typical coverage

  const roi = {
    premiumCost: avgPremium,
    potentialPayout: typicalPayout,
    outOfPocketWithout,
    outOfPocketWith,
    savingsRatio: outOfPocketWithout / outOfPocketWith
  };

  // Generate questions to ask insurers based on profile
  const questionsForInsurer: QuestionForInsurer[] = [
    // Coverage Questions
    {
      category: 'Coverage Limits',
      question: 'What is the aggregate limit vs. per-occurrence limit for cyber incidents?',
      whyItMatters: 'Multiple incidents could exhaust aggregate limits. Ensure per-occurrence is sufficient for your breach cost estimate.'
    },
    {
      category: 'Coverage Limits',
      question: 'Are there sub-limits for specific coverage types (ransomware, business interruption, regulatory fines)?',
      whyItMatters: 'Sub-limits can significantly reduce payouts. Know the limits for your highest-risk scenarios.'
    },
    {
      category: 'Coverage Scope',
      question: 'Does the policy cover third-party service provider failures (cloud, SaaS vendors)?',
      whyItMatters: 'Many breaches originate from vendor compromises. Ensure contingent coverage is included.'
    },
    {
      category: 'Coverage Scope',
      question: 'Is social engineering and business email compromise (BEC) fraud covered?',
      whyItMatters: 'BEC attacks cost businesses $2.7B+ annually. This coverage often requires a separate endorsement.'
    },
    // Industry-specific questions
    ...(hasPHI ? [{
      category: 'Healthcare/HIPAA',
      question: 'Does the policy specifically cover HIPAA fines and OCR investigation costs?',
      whyItMatters: 'HIPAA fines can reach $1.5M+ per violation category. Ensure regulatory coverage applies to OCR.'
    }] : []),
    ...(hasPCI ? [{
      category: 'Payment Cards',
      question: 'Are PCI DSS fines, forensic investigation costs, and card reissuance covered?',
      whyItMatters: 'Card brand fines and fraud chargebacks can be substantial. Some policies exclude these.'
    }] : []),
    // Claims Process
    {
      category: 'Claims Process',
      question: 'What is the typical claims response time and do you have a 24/7 breach hotline?',
      whyItMatters: 'Fast response is critical. Every hour of delay increases breach costs.'
    },
    {
      category: 'Claims Process',
      question: 'Can I select my own incident response vendor or must I use panel providers?',
      whyItMatters: 'Pre-existing IR relationships may be critical. Panel-only requirements could cause delays.'
    },
    {
      category: 'Claims Process',
      question: 'What documentation is required to file a claim and what is the typical adjudication timeline?',
      whyItMatters: 'Complex documentation requirements can delay payouts when you need funds most.'
    },
    // Exclusions
    {
      category: 'Exclusions',
      question: 'What are the policy exclusions for nation-state attacks and acts of war?',
      whyItMatters: 'Many attacks are attributed to nation-states. Broad war exclusions could void coverage.'
    },
    {
      category: 'Exclusions',
      question: 'Is there a retroactive date or prior acts exclusion?',
      whyItMatters: 'Breaches often go undetected for months. Ensure coverage for incidents that started before policy inception.'
    },
    {
      category: 'Exclusions',
      question: 'How does the policy handle unencrypted data, unpatched systems, or known vulnerabilities?',
      whyItMatters: 'Some policies exclude claims where basic security hygiene was lacking. Understand the requirements.'
    },
    // Renewal & Pricing
    {
      category: 'Renewal & Pricing',
      question: 'How is the premium calculated at renewal, especially after a claim?',
      whyItMatters: 'Post-claim premium increases can be substantial (2-3x). Understand the pricing model upfront.'
    },
    {
      category: 'Renewal & Pricing',
      question: 'What security improvements would qualify for premium discounts?',
      whyItMatters: 'Your readiness score is ${readinessScore}%. Know which investments provide premium benefits.'
    },
    ...(geographicScope === 'international' ? [{
      category: 'International',
      question: 'Does the policy provide worldwide coverage and comply with local regulations (GDPR, etc.)?',
      whyItMatters: 'International operations face multi-jurisdictional requirements. Ensure global coverage applies.'
    }] : [])
  ];

  return {
    recommendedCoverageLimit: recommendedLimit,
    coverageRange,
    estimatedPremium,
    deductibleRange,
    coverageTypes,
    premiumFactors,
    readinessScore,
    readinessItems: readinessItemsList,
    roi,
    questionsForInsurer
  };
}

function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${Math.round(value / 1000)}K`;
  }
  return `$${Math.round(value).toLocaleString()}`;
}

function formatCurrencyFull(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}

// ============================================================================
// COMPONENT
// ============================================================================

export function CyberInsuranceTab({
  industry,
  region,
  annualRevenue,
  recordCount,
  maturityLevel,
  hasIR,
  breachCostResult,
  dataTypes = ['customer_pii']
}: CyberInsuranceTabProps) {
  // Additional inputs for insurance calculation
  const [geographicScope, setGeographicScope] = useState('us_only');
  const [priorBreach, setPriorBreach] = useState('none');
  const [annualTransactionVolume, setAnnualTransactionVolume] = useState(100000);
  const [showRecommendation, setShowRecommendation] = useState(false);
  const [recommendation, setRecommendation] = useState<InsuranceRecommendation | null>(null);

  // Readiness checklist
  const [readinessChecklist, setReadinessChecklist] = useState<Record<string, boolean>>({
    mfa: false,
    edr: false,
    backup: false,
    irPlan: hasIR,
    training: false,
    patching: false,
    encryption: false,
    accessControl: false
  });

  // Update IR status when prop changes
  useEffect(() => {
    setReadinessChecklist(prev => ({ ...prev, irPlan: hasIR }));
  }, [hasIR]);

  const toggleReadinessItem = (id: string) => {
    setReadinessChecklist(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const calculateRecommendation = () => {
    // Use breach cost result if available, otherwise estimate
    const estimatedBreachCost = breachCostResult?.totalCost ||
      (recordCount * 165 * (industry === 'healthcare' ? 2.24 : 1.0));

    const rec = calculateInsuranceRecommendation(
      industry,
      region,
      annualRevenue,
      recordCount,
      maturityLevel,
      hasIR,
      geographicScope,
      priorBreach,
      readinessChecklist,
      estimatedBreachCost,
      dataTypes,
      annualTransactionVolume
    );

    setRecommendation(rec);
    setShowRecommendation(true);
  };

  // Helper to format data type labels
  const formatDataType = (dt: DataType): string => {
    const labels: Record<DataType, string> = {
      customer_pii: 'Personal Information (PII)',
      pci: 'Payment Card Data (PCI)',
      phi: 'Protected Health Info (PHI)',
      ssn_tax_id: 'SSN / Tax IDs',
      financial_records: 'Financial Records',
      ip_trade_secrets: 'Intellectual Property',
      employee_records: 'Employee Records',
      behavioral_data: 'Behavioral Data',
      credentials: 'Credentials'
    };
    return labels[dt] || dt;
  };

  const getImpactColor = (impact: 'critical' | 'high' | 'medium') => {
    switch (impact) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-blue-600 bg-blue-50 border-blue-200';
    }
  };

  const getImpactBadge = (impact: 'critical' | 'high' | 'medium') => {
    switch (impact) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-blue-100 text-blue-800';
    }
  };

  if (!showRecommendation) {
    return (
      <div className="space-y-6">
        {/* Context Banner */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="text-3xl">{"🛡️"}</div>
            <div>
              <h3 className="text-lg font-bold text-blue-900 mb-2">
                Cyber Insurance Recommendation Tool
              </h3>
              <p className="text-blue-700">
                Get personalized cyber insurance coverage recommendations based on your organization{"'"}s
                risk profile. We{"'"}ll analyze your industry, data exposure, and security posture to
                estimate coverage needs and premium ranges.
              </p>
              {breachCostResult && (
                <p className="text-blue-700 mt-2 font-medium">
                  Using your breach cost estimate of {formatCurrencyFull(breachCostResult.totalCost)} for recommendations.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Inherited Values Display */}
        <CalculatorCard>
          <CalculatorSection
            title="Organization Profile"
            description="These values are inherited from the Breach Cost Calculator. Switch tabs to modify."
            icon="🏢"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600">Industry</div>
                <div className="font-semibold text-gray-900 capitalize">{industry.replace(/_/g, ' ')}</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600">Annual Revenue</div>
                <div className="font-semibold text-gray-900">{formatCurrencyFull(annualRevenue)}</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600">Records at Risk</div>
                <div className="font-semibold text-gray-900">{recordCount.toLocaleString()}</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600">Security Maturity</div>
                <div className="font-semibold text-gray-900 capitalize">{maturityLevel}</div>
              </div>
            </div>
            {/* Data Types Display */}
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-600 mb-2">Data Types Handled</div>
              <div className="flex flex-wrap gap-2">
                {dataTypes.map((dt) => (
                  <span
                    key={dt}
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      dt === 'phi' ? 'bg-red-100 text-red-800' :
                      dt === 'pci' ? 'bg-orange-100 text-orange-800' :
                      dt === 'ssn_tax_id' ? 'bg-yellow-100 text-yellow-800' :
                      dt === 'ip_trade_secrets' ? 'bg-purple-100 text-purple-800' :
                      'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {formatDataType(dt)}
                  </span>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Data types affect coverage recommendations and premium calculations
              </p>
            </div>
          </CalculatorSection>
        </CalculatorCard>

        {/* Additional Inputs */}
        <CalculatorCard>
          <CalculatorSection
            title="Insurance-Specific Factors"
            description="Additional information needed for insurance recommendations"
            icon="📋"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectInput
                label="Geographic Scope"
                value={geographicScope}
                onChange={setGeographicScope}
                options={GEOGRAPHIC_SCOPE_OPTIONS}
                helpText="Where do you collect/process customer data?"
              />
              <SelectInput
                label="Prior Breach History"
                value={priorBreach}
                onChange={setPriorBreach}
                options={PRIOR_BREACH_OPTIONS}
                helpText="Have you experienced a data breach?"
              />
            </div>
            <div className="mt-4">
              <DualInputWithSlider
                label="Annual Transaction Volume"
                value={annualTransactionVolume}
                onChange={setAnnualTransactionVolume}
                min={1000}
                max={100000000}
                helpText="Number of transactions processed per year (credit card, wire transfers, etc.)"
              />
            </div>
          </CalculatorSection>
        </CalculatorCard>

        {/* Insurance Readiness Checklist */}
        <CalculatorCard>
          <CalculatorSection
            title="Insurance Readiness Checklist"
            description="These controls are often required by cyber insurers and can significantly reduce premiums"
            icon="✅"
          >
            <div className="space-y-3">
              {[
                { id: 'mfa', name: 'Multi-Factor Authentication (MFA)', impact: 'critical' as const, desc: 'MFA enabled for all remote access and privileged accounts' },
                { id: 'edr', name: 'Endpoint Detection & Response (EDR)', impact: 'critical' as const, desc: 'EDR deployed on all endpoints with 24/7 monitoring' },
                { id: 'backup', name: 'Offline/Immutable Backups', impact: 'critical' as const, desc: 'Regular backups stored offline or immutable, tested quarterly' },
                { id: 'irPlan', name: 'Incident Response Plan', impact: 'high' as const, desc: 'Documented IR plan tested at least annually' },
                { id: 'training', name: 'Security Awareness Training', impact: 'high' as const, desc: 'Regular phishing simulations and security training' },
                { id: 'patching', name: 'Vulnerability Management', impact: 'high' as const, desc: 'Regular scanning with critical patches within 30 days' },
                { id: 'encryption', name: 'Data Encryption', impact: 'medium' as const, desc: 'Encryption at rest and in transit for sensitive data' },
                { id: 'accessControl', name: 'Privileged Access Management', impact: 'medium' as const, desc: 'Least privilege access, separate admin accounts' }
              ].map((item) => (
                <label
                  key={item.id}
                  className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    readinessChecklist[item.id]
                      ? 'bg-green-50 border-green-300'
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={readinessChecklist[item.id]}
                    onChange={() => toggleReadinessItem(item.id)}
                    className="mt-1 w-5 h-5 text-green-500 border-gray-300 rounded focus:ring-2 focus:ring-green-200"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">{item.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-bold uppercase ${getImpactBadge(item.impact)}`}>
                        {item.impact}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 mt-1">{item.desc}</div>
                  </div>
                </label>
              ))}
            </div>

            {/* Readiness Score Preview */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-gray-700">Readiness Score</span>
                <span className="text-lg font-bold text-blue-600">
                  {Math.round(
                    (Object.entries(readinessChecklist).filter(([k, v]) => {
                      const item = ['mfa', 'edr', 'backup'].includes(k) ? 'critical'
                        : ['irPlan', 'training', 'patching'].includes(k) ? 'high' : 'medium';
                      return v;
                    }).length / 8) * 100
                  )}%
                </span>
              </div>
              <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#2cc3d1] to-[#2813e8] transition-all duration-500"
                  style={{
                    width: `${Math.round(
                      (Object.entries(readinessChecklist).filter(([, v]) => v).length / 8) * 100
                    )}%`
                  }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Higher readiness scores can reduce premiums by up to 25%
              </p>
            </div>
          </CalculatorSection>

          <div className="flex justify-end mt-6">
            <button
              onClick={calculateRecommendation}
              className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Get Insurance Recommendations
            </button>
          </div>
        </CalculatorCard>
      </div>
    );
  }

  // Results view
  if (!recommendation) return null;

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={() => setShowRecommendation(false)}
        className="text-orange-500 hover:text-orange-600 font-semibold flex items-center gap-2"
      >
        {"<-"} Modify Inputs
      </button>

      {/* Coverage Recommendation Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="text-4xl">{"🛡️"}</div>
          <div>
            <h3 className="text-2xl font-bold mb-2">
              Recommended Coverage: {formatCurrencyFull(recommendation.recommendedCoverageLimit)}
            </h3>
            <p className="text-blue-100">
              Based on your risk profile, we recommend coverage between {formatCurrency(recommendation.coverageRange.min)} and {formatCurrency(recommendation.coverageRange.max)}
            </p>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <MetricCard
          label="Estimated Annual Premium"
          value={`${formatCurrency(recommendation.estimatedPremium.low)} - ${formatCurrency(recommendation.estimatedPremium.high)}`}
          description="Based on market benchmarks"
          icon="💵"
          color="blue"
          tooltip="Premium estimates based on industry benchmarks, your risk profile, and security posture. Actual premiums will vary by insurer."
        />
        <MetricCard
          label="Recommended Deductible"
          value={`${formatCurrency(recommendation.deductibleRange.low)} - ${formatCurrency(recommendation.deductibleRange.high)}`}
          description="Higher deductible = lower premium"
          icon="📊"
          color="orange"
          tooltip="The amount you pay before insurance kicks in. Higher deductibles reduce premiums but increase out-of-pocket costs."
        />
        <MetricCard
          label="Readiness Score"
          value={`${recommendation.readinessScore}%`}
          description={recommendation.readinessScore >= 75 ? 'Excellent' : recommendation.readinessScore >= 50 ? 'Good' : 'Needs improvement'}
          icon="✅"
          color={recommendation.readinessScore >= 75 ? 'green' : recommendation.readinessScore >= 50 ? 'blue' : 'orange'}
          tooltip="Based on implementing security controls that insurers typically require. Higher scores often result in lower premiums and better coverage terms."
        />
        <MetricCard
          label="Insurance ROI"
          value={`${recommendation.roi.savingsRatio.toFixed(1)}x`}
          description="Potential savings vs. uninsured"
          icon="📈"
          color="green"
          tooltip={`Without insurance, a breach could cost ${formatCurrencyFull(recommendation.roi.outOfPocketWithout)}. With insurance (premium + deductible), your out-of-pocket would be approximately ${formatCurrencyFull(recommendation.roi.outOfPocketWith)}.`}
        />
      </div>

      {/* ROI Analysis */}
      <CalculatorCard>
        <h3 className="text-xl font-bold text-gray-900 mb-4">Insurance ROI Analysis</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
            <h4 className="font-bold text-red-900 mb-4">Without Cyber Insurance</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-700">Potential Breach Cost</span>
                <span className="font-bold text-red-600">{formatCurrencyFull(recommendation.roi.outOfPocketWithout)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Your Out-of-Pocket</span>
                <span className="font-bold text-red-600">{formatCurrencyFull(recommendation.roi.outOfPocketWithout)}</span>
              </div>
              <p className="text-sm text-red-700 mt-4">
                100% of breach costs come out of your pocket, potentially threatening business continuity.
              </p>
            </div>
          </div>
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
            <h4 className="font-bold text-green-900 mb-4">With Cyber Insurance</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-700">Annual Premium</span>
                <span className="font-semibold text-gray-900">{formatCurrencyFull(recommendation.roi.premiumCost)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Typical Deductible</span>
                <span className="font-semibold text-gray-900">{formatCurrency((recommendation.deductibleRange.low + recommendation.deductibleRange.high) / 2)}</span>
              </div>
              <div className="flex justify-between border-t border-green-300 pt-2">
                <span className="text-gray-700 font-semibold">Your Out-of-Pocket</span>
                <span className="font-bold text-green-600">{formatCurrencyFull(recommendation.roi.outOfPocketWith)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Insurance Payout</span>
                <span className="font-semibold text-green-600">Up to {formatCurrencyFull(recommendation.roi.potentialPayout)}</span>
              </div>
            </div>
          </div>
        </div>
      </CalculatorCard>

      {/* Coverage Types */}
      <CalculatorCard>
        <h3 className="text-xl font-bold text-gray-900 mb-6">Recommended Coverage Types</h3>

        {/* First-Party Coverage */}
        <div className="mb-8">
          <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="text-blue-500">{"⬛"}</span>
            First-Party Coverage
            <InfoIcon tooltip="Covers your direct losses and expenses from a cyber incident" />
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recommendation.coverageTypes.firstParty.map((item, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border-2 ${
                  item.recommended ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-semibold text-gray-900 flex items-center gap-2">
                      {item.name}
                      {item.recommended && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-500 text-white">Recommended</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                    {item.estimatedValue && (
                      <p className="text-sm font-medium text-blue-600 mt-2">{item.estimatedValue}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Third-Party Coverage */}
        <div className="mb-8">
          <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="text-purple-500">{"⬛"}</span>
            Third-Party Coverage
            <InfoIcon tooltip="Covers claims and liabilities from others affected by your breach" />
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recommendation.coverageTypes.thirdParty.map((item, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border-2 ${
                  item.recommended ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-semibold text-gray-900 flex items-center gap-2">
                      {item.name}
                      {item.recommended && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500 text-white">Recommended</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                    {item.estimatedValue && (
                      <p className="text-sm font-medium text-purple-600 mt-2">{item.estimatedValue}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Endorsements */}
        <div>
          <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="text-orange-500">{"⬛"}</span>
            Optional Endorsements
            <InfoIcon tooltip="Additional coverage options that may be added to your policy" />
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recommendation.coverageTypes.endorsements.map((item, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border-2 ${
                  item.recommended ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-semibold text-gray-900 flex items-center gap-2">
                      {item.name}
                      {item.recommended && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500 text-white">Consider</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                    {item.estimatedValue && (
                      <p className="text-sm font-medium text-orange-700 mt-2">{item.estimatedValue}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CalculatorCard>

      {/* Premium Factors */}
      <CalculatorCard>
        <h3 className="text-xl font-bold text-gray-900 mb-4">Factors Affecting Your Premium</h3>
        <div className="space-y-3">
          {recommendation.premiumFactors.map((factor, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg border-2 flex items-center justify-between ${
                factor.impact === 'increase'
                  ? 'bg-red-50 border-red-200'
                  : factor.impact === 'decrease'
                  ? 'bg-green-50 border-green-200'
                  : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div>
                <div className="font-semibold text-gray-900">{factor.factor}</div>
                <div className="text-sm text-gray-600">{factor.description}</div>
              </div>
              <div className={`text-lg font-bold ${
                factor.impact === 'increase' ? 'text-red-600' : factor.impact === 'decrease' ? 'text-green-600' : 'text-gray-600'
              }`}>
                {factor.impact === 'increase' ? '+' : factor.impact === 'decrease' ? '-' : ''}
                {factor.percentage}%
              </div>
            </div>
          ))}
        </div>
      </CalculatorCard>

      {/* Readiness Checklist Results */}
      <CalculatorCard>
        <h3 className="text-xl font-bold text-gray-900 mb-4">Insurance Readiness Assessment</h3>
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-gray-700">Overall Readiness Score</span>
            <span className={`text-2xl font-bold ${
              recommendation.readinessScore >= 75 ? 'text-green-600'
                : recommendation.readinessScore >= 50 ? 'text-blue-600'
                : 'text-orange-600'
            }`}>
              {recommendation.readinessScore}%
            </span>
          </div>
          <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${
                recommendation.readinessScore >= 75 ? 'bg-green-500'
                  : recommendation.readinessScore >= 50 ? 'bg-blue-500'
                  : 'bg-orange-500'
              }`}
              style={{ width: `${recommendation.readinessScore}%` }}
            />
          </div>
        </div>

        <div className="space-y-3">
          {recommendation.readinessItems.map((item) => (
            <div
              key={item.id}
              className={`p-4 rounded-lg border-2 ${
                item.implemented
                  ? 'bg-green-50 border-green-300'
                  : getImpactColor(item.impact)
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`mt-0.5 text-lg ${item.implemented ? 'text-green-500' : 'text-gray-400'}`}>
                  {item.implemented ? '✓' : '○'}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`font-semibold ${item.implemented ? 'text-green-900' : 'text-gray-900'}`}>
                      {item.name}
                    </span>
                    {!item.implemented && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-bold uppercase ${getImpactBadge(item.impact)}`}>
                        {item.impact}
                      </span>
                    )}
                  </div>
                  <p className={`text-sm mt-1 ${item.implemented ? 'text-green-700' : 'text-gray-600'}`}>
                    {item.description}
                  </p>
                  <p className={`text-sm mt-1 font-medium ${item.implemented ? 'text-green-600' : 'text-blue-600'}`}>
                    {item.premiumImpact}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CalculatorCard>

      {/* Questions to Ask Your Insurer */}
      <CalculatorCard>
        <h3 className="text-xl font-bold text-gray-900 mb-4">Questions to Ask Your Insurer</h3>
        <p className="text-gray-600 mb-6">
          Use these questions during your cyber insurance shopping process. They are tailored based on your
          organization{"'"}s risk profile, data types, and coverage needs.
        </p>

        {/* Group questions by category */}
        {(() => {
          const categories = [...new Set(recommendation.questionsForInsurer.map(q => q.category))];
          return categories.map((category) => (
            <div key={category} className="mb-6 last:mb-0">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                {category}
              </h4>
              <div className="space-y-3 ml-4">
                {recommendation.questionsForInsurer
                  .filter(q => q.category === category)
                  .map((question, index) => (
                    <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="font-medium text-gray-900 mb-2">
                        {"Q: "}{question.question}
                      </div>
                      <div className="text-sm text-gray-600 flex items-start gap-2">
                        <span className="text-blue-500 font-bold">Why it matters:</span>
                        <span>{question.whyItMatters}</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ));
        })()}
      </CalculatorCard>

      {/* Action Buttons */}
      <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
        <button
          onClick={() => setShowRecommendation(false)}
          className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold transition-colors"
        >
          {"<-"} Recalculate
        </button>
        <button
          onClick={() => {
            generateCyberInsurancePDF({
              recommendation,
              inputs: {
                industry,
                region,
                annualRevenue,
                recordCount,
                maturityLevel,
                hasIR,
                geographicScope,
                priorBreach,
                annualTransactionVolume,
                dataTypes,
                readinessChecklist
              },
              breachCostEstimate: breachCostResult?.totalCost ||
                (recordCount * 165 * (industry === 'healthcare' ? 2.24 : 1.0))
            });
          }}
          className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
        >
          Download PDF Report
        </button>
        {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
        <a
          href="/contact"
          className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors text-center"
        >
          Get Insurance Quote Help
        </a>
      </div>
    </div>
  );
}
